import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query"
import {
  addHome,
  bulkImportHomes,
  cancelBatchImport,
  type BulkImportAddress,
  createPortfolio,
  deleteHome,
  deletePortfolio,
  getBatchImportAddresses,
  getBatchImportStatus,
  getDashboardStats,
  getHeatLoss,
  getHomeEmbodiedCarbon,
  getHomes,
  getHomesPaginated,
  getLatestProcessingBatchImport,
  getPortfolioCriteria,
  getPortfolioStats,
  type Home,
  listPortfolios,
  markHomeInterested,
  moveHomeToPortfolio,
  onboardingSetup,
  renamePortfolio,
  savePortfolioCriteria,
} from "../services/portfolioApi"

export const useHomes = (portfolioId?: number) => {
  return useQuery({
    queryKey: ["homes", portfolioId],
    queryFn: () => getHomes(portfolioId),
    enabled: !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useInfiniteHomes = (
  limit: number = 20,
  portfolioId?: number,
  search?: string,
  builtForm?: string,
  sort?: string,
  roiPotential?: string,
  minEpcBand?: string,
  maxEpcBand?: string,
  epcBands?: string[]
) => {
  const epcBandsKey = epcBands?.join(",") || undefined
  return useInfiniteQuery({
    queryKey: ["homes", "infinite", limit, portfolioId, search, builtForm, sort, roiPotential, minEpcBand, maxEpcBand, epcBandsKey],
    queryFn: ({ pageParam }) => getHomesPaginated({ cursor: pageParam, limit, portfolioId, search, builtForm, sort: sort as any, roiPotential, minEpcBand, maxEpcBand, epcBands }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: !!localStorage.getItem("token"),
    // Filters are part of the query key, so without this every filter change drops to
    // isLoading and unmounts the filter bar mid-interaction (closing any open dropdown).
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

export const usePortfolios = () => {
  return useQuery({
    queryKey: ["portfolios"],
    queryFn: listPortfolios,
    enabled: !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreatePortfolio = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => createPortfolio(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolios"] })
    },
  })
}

export const useDeletePortfolio = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deletePortfolio(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolios"] })
      queryClient.invalidateQueries({ queryKey: ["userDetails"] })
    },
  })
}

export const useRenamePortfolio = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => renamePortfolio(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolios"] })
      queryClient.invalidateQueries({ queryKey: ["homes"] })
    },
  })
}

export const useAddHome = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: any) => addHome(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homes"] })
      queryClient.invalidateQueries({ queryKey: ["portfolioStats"] })
    },
  })
}

export const useDeleteHome = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (homeId: number) => deleteHome(homeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homes"] })
      queryClient.invalidateQueries({ queryKey: ["portfolioStats"] })
      queryClient.invalidateQueries({ queryKey: ["kanbanCards"] })
      queryClient.invalidateQueries({ queryKey: ["opportunities"] })
    },
  })
}

export const useMarkHomeInterested = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (homeId: number) => markHomeInterested(homeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homes"] })
    },
  })
}

export const useHeatLoss = (homeId: number) => {
  return useQuery({
    queryKey: ["heatLoss", homeId],
    queryFn: () => getHeatLoss(homeId),
    enabled: !!homeId && !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useBulkImportHomes = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { addresses: BulkImportAddress[]; portfolioId?: number }) => bulkImportHomes(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homes"] })
      queryClient.invalidateQueries({ queryKey: ["portfolioStats"] })
    },
  })
}

export const useBatchImportStatus = (batchImportId: number | null) => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ["batchImport", batchImportId],
    queryFn: () => getBatchImportStatus(batchImportId!),
    enabled: !!batchImportId && !!localStorage.getItem("token"),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === "completed" || status === "cancelled") {
        queryClient.invalidateQueries({ queryKey: ["homes"] })
        return false
      }
      return 10000
    },
  })
}

export const useBatchImportAddresses = (batchImportId: number | null) => {
  return useInfiniteQuery({
    queryKey: ["batchImportAddresses", batchImportId],
    queryFn: ({ pageParam = 1 }) => getBatchImportAddresses(batchImportId!, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    enabled: !!batchImportId && !!localStorage.getItem("token"),
  })
}

export const useCancelBatchImport = (batchImportId: number | null) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => cancelBatchImport(batchImportId!),
    onSuccess: (data) => {
      queryClient.setQueryData(["batchImport", batchImportId], data)
      queryClient.invalidateQueries({ queryKey: ["batchImportAddresses", batchImportId] })
    },
  })
}

export const useLatestProcessingBatchImport = () => {
  return useQuery({
    queryKey: ["latestProcessingBatchImport"],
    queryFn: getLatestProcessingBatchImport,
    enabled: !!localStorage.getItem("token"),
    retry: false,
  })
}

export const useDashboardStats = () => {
  return useQuery({
    queryKey: ["dashboardStats"],
    queryFn: getDashboardStats,
    enabled: !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const usePortfolioStats = (portfolioId?: number) => {
  return useQuery({
    queryKey: ["portfolioStats", portfolioId],
    queryFn: () => getPortfolioStats(portfolioId),
    enabled: !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000,
  })
}

export const usePortfolioCriteria = (portfolioId?: number) =>
  useQuery({
    queryKey: ['portfolioCriteria', portfolioId],
    queryFn: () => getPortfolioCriteria(portfolioId!),
    enabled: !!portfolioId,
    staleTime: 5 * 60 * 1000,
  })

export const useSavePortfolioCriteria = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ portfolioId, data }: { portfolioId: number; data: any }) =>
      savePortfolioCriteria(portfolioId, data),
    onSuccess: (_, { portfolioId }) => {
      queryClient.invalidateQueries({ queryKey: ['portfolioCriteria', portfolioId] })
    },
  })
}

export const useMoveHomeToPortfolio = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ homeId, targetPortfolioId, opportunityId }: { homeId: number; targetPortfolioId: number; opportunityId?: number }) =>
      moveHomeToPortfolio(homeId, targetPortfolioId, opportunityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homes'] })
      queryClient.invalidateQueries({ queryKey: ['portfolioStats'] })
      queryClient.invalidateQueries({ queryKey: ['kanban'] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export const useOnboardingSetup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; [key: string]: any }) => onboardingSetup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolios'] })
      queryClient.invalidateQueries({ queryKey: ['userDetails'] })
      queryClient.invalidateQueries({ queryKey: ['portfolioCriteria'] })
    },
  })
}

export interface PortfolioEmbodiedCarbonSummary {
  totalEmbodiedKgCo2e: number
  carbonPaybackYears: number | null
}

export const usePortfolioEmbodiedCarbon = (homes: Home[], totalCo2SavingsKg: number | null) => {
  const analysedHomeIds = homes
    .filter((h) => h.status === "analysed" && h.upgradesTotalCost != null)
    .map((h) => h.id)

  return useQuery<PortfolioEmbodiedCarbonSummary>({
    queryKey: ["portfolioEmbodiedCarbon", analysedHomeIds, totalCo2SavingsKg],
    queryFn: async () => {
      const results = await Promise.allSettled(
        analysedHomeIds.map((id) => getHomeEmbodiedCarbon(id))
      )
      const totalEmbodiedKgCo2e = results.reduce((sum, r) => {
        if (r.status === "fulfilled" && r.value.totalEmbodiedCarbonKgco2e != null) {
          return sum + Number(r.value.totalEmbodiedCarbonKgco2e)
        }
        return sum
      }, 0)
      const annualSavings = totalCo2SavingsKg ?? 0
      const carbonPaybackYears =
        annualSavings > 0 ? totalEmbodiedKgCo2e / annualSavings : null
      return { totalEmbodiedKgCo2e, carbonPaybackYears }
    },
    enabled: analysedHomeIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}
